import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, BarChart3, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { getPlayers, createPlayer, updatePlayer, deletePlayer, updatePlayerStats, getTopScorers } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

const teamCategories = ["Eerste ploeg", "Jong FTG", "C", "U17", "U15", "U13"];
const positions = ["Keeper", "Defender", "Winger", "Pivot"];
const preferredFoot = ["Right", "Left", "Both"];

const initialFormData = {
  name: "",
  position: "Defender",
  number: "",
  image_url: "",
  team_category: "A",
  nationality: "",
  date_of_birth: "",
  height: "",
  preferred_foot: "Right",
};

const initialStats = {
  goals: 0,
  assists: 0,
  appearances: 0,
  yellow_cards: 0,
  red_cards: 0,
  clean_sheets: 0,
  minutes_played: 0,
};

export default function AdminPlayers() {
  const [players, setPlayers] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [statsData, setStatsData] = useState(initialStats);
  const [editingId, setEditingId] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("players");

  const fetchPlayers = async () => {
    try {
      const params = categoryFilter !== "all" ? { category: categoryFilter } : {};
      const [playersRes, scorersRes] = await Promise.all([
        getPlayers(params),
        getTopScorers({ limit: 10 })
      ]);
      setPlayers(playersRes.data);
      setTopScorers(scorersRes.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, [categoryFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleStatsChange = (e) => {
    const { name, value } = e.target;
    setStatsData({ ...statsData, [name]: parseInt(value) || 0 });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (url) => {
    setFormData({ ...formData, image_url: url });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...formData,
      number: parseInt(formData.number),
      height: formData.height ? parseInt(formData.height) : null,
    };

    try {
      if (editingId) {
        await updatePlayer(editingId, data);
        toast.success("Player updated successfully");
      } else {
        await createPlayer(data);
        toast.success("Player created successfully");
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setEditingId(null);
      fetchPlayers();
    } catch (error) {
      toast.error("Failed to save player");
      console.error("Error saving player:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStats = async () => {
    setSaving(true);
    try {
      await updatePlayerStats(editingPlayer.id, statsData);
      toast.success("Stats updated successfully");
      setStatsDialogOpen(false);
      fetchPlayers();
    } catch (error) {
      toast.error("Kon statistieken niet bijwerken");
      console.error("Error updating stats:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (player) => {
    setFormData({
      name: player.name,
      position: player.position,
      number: player.number.toString(),
      image_url: player.image_url || "",
      team_category: player.team_category,
      nationality: player.nationality || "",
      date_of_birth: player.date_of_birth || "",
      height: player.height ? player.height.toString() : "",
      preferred_foot: player.preferred_foot || "Right",
    });
    setEditingId(player.id);
    setDialogOpen(true);
  };

  const handleEditStats = (player) => {
    setEditingPlayer(player);
    setStatsData({
      goals: player.goals || 0,
      assists: player.assists || 0,
      appearances: player.appearances || 0,
      yellow_cards: player.yellow_cards || 0,
      red_cards: player.red_cards || 0,
      clean_sheets: player.clean_sheets || 0,
      minutes_played: player.minutes_played || 0,
    });
    setStatsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je deze speler wilt verwijderen?")) return;
    try {
      await deletePlayer(id);
      toast.success("Speler verwijderd");
      fetchPlayers();
    } catch (error) {
      toast.error("Kon speler niet verwijderen");
    }
  };

  const openNewDialog = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setDialogOpen(true);
  };

  const positionColors = {
    Keeper: "bg-yellow-500",
    Defender: "bg-blue-500",
    Winger: "bg-green-500",
    Pivot: "bg-red-500",
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  return (
    <div data-testid="admin-players-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Spelers</h1>
          <p className="text-slate-500">Beheer teamrosters en statistieken</p>
        </div>
        <div className="flex gap-4">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[150px]" data-testid="player-category-filter">
              <SelectValue placeholder="Filteren op team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Teams</SelectItem>
              {teamCategories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat} Team</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="add-player-btn">
            <Plus className="h-4 w-4 mr-2" />
            Speler Toevoegen
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="players" className="gap-2">
            <Users className="h-4 w-4" /> Spelers
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2">
            <BarChart3 className="h-4 w-4" /> Top Statistieken
          </TabsTrigger>
        </TabsList>

        <TabsContent value="players">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-16">#</TableHead>
                      <TableHead>Speler</TableHead>
                      <TableHead>Positie</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead className="text-center">D</TableHead>
                      <TableHead className="text-center">A</TableHead>
                      <TableHead className="text-center">Wed</TableHead>
                      <TableHead className="text-right">Acties</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">Laden...</TableCell>
                      </TableRow>
                    ) : players.length > 0 ? (
                      players.map((player) => (
                        <TableRow key={player.id} data-testid={`player-row-${player.id}`}>
                          <TableCell className="font-bold text-slate-400">{player.number}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {player.image_url ? (
                                <img 
                                  src={getImageUrl(player.image_url)} 
                                  alt={player.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                                  <span className="text-slate-500 font-bold">{player.name.charAt(0)}</span>
                                </div>
                              )}
                              <div>
                                <p className="font-medium">{player.name}</p>
                                {player.nationality && (
                                  <p className="text-xs text-slate-500">{player.nationality}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={positionColors[player.position] || "bg-slate-500"}>
                              {player.position}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{player.team_category}</Badge>
                          </TableCell>
                          <TableCell className="text-center font-medium">{player.goals || 0}</TableCell>
                          <TableCell className="text-center font-medium">{player.assists || 0}</TableCell>
                          <TableCell className="text-center">{player.appearances || 0}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditStats(player)}
                                title="Bewerken Stats"
                                data-testid={`stats-player-${player.id}`}
                              >
                                <BarChart3 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEdit(player)}
                                data-testid={`edit-player-${player.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDelete(player.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-player-${player.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          Geen spelers gevonden. Voeg je eerste speler toe!
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Top Scorers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topScorers.filter(p => (p.goals || 0) > 0).slice(0, 5).map((player, idx) => (
                    <div key={player.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>{idx + 1}</span>
                        <div>
                          <p className="font-medium">{player.name}</p>
                          <p className="text-xs text-slate-500">{player.team_category} Team</p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-blue-600">{player.goals || 0}</span>
                    </div>
                  ))}
                  {topScorers.filter(p => (p.goals || 0) > 0).length === 0 && (
                    <p className="text-center text-slate-500 py-4">Nog geen doelpunten geregistreerd</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Top Assists</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topScorers.sort((a,b) => (b.assists || 0) - (a.assists || 0))
                    .filter(p => (p.assists || 0) > 0).slice(0, 5).map((player, idx) => (
                    <div key={player.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                          idx === 1 ? 'bg-slate-300 text-slate-700' :
                          idx === 2 ? 'bg-amber-600 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>{idx + 1}</span>
                        <div>
                          <p className="font-medium">{player.name}</p>
                          <p className="text-xs text-slate-500">{player.team_category} Team</p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-green-600">{player.assists || 0}</span>
                    </div>
                  ))}
                  {topScorers.filter(p => (p.assists || 0) > 0).length === 0 && (
                    <p className="text-center text-slate-500 py-4">Nog geen assists geregistreerd</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Player Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Speler Bewerken" : "Speler Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ImageUpload 
              value={formData.image_url}
              onChange={handleImageChange}
              label="Speler Foto"
            />

            <div className="space-y-2">
              <Label htmlFor="name">Naam *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required data-testid="player-name" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="number">Rugnummer *</Label>
                <Input id="number" name="number" type="number" min="1" max="99" value={formData.number} onChange={handleChange} required data-testid="player-number" />
              </div>
              <div className="space-y-2">
                <Label>Positie *</Label>
                <Select value={formData.position} onValueChange={(v) => handleSelectChange("position", v)}>
                  <SelectTrigger data-testid="player-position"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (<SelectItem key={pos} value={pos}>{pos}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team *</Label>
                <Select value={formData.team_category} onValueChange={(v) => handleSelectChange("team_category", v)}>
                  <SelectTrigger data-testid="player-team"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {teamCategories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sterke Voet</Label>
                <Select value={formData.preferred_foot} onValueChange={(v) => handleSelectChange("preferred_foot", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {preferredFoot.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nationality">Nationaliteit</Label>
                <Input id="nationality" name="nationality" value={formData.nationality} onChange={handleChange} placeholder="bijv. Belgisch" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="height">Lengte (cm)</Label>
                <Input id="height" name="height" type="number" value={formData.height} onChange={handleChange} placeholder="175" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Geboortedatum</Label>
              <Input id="date_of_birth" name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} />
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="player-save-btn">
                {saving ? "Opslaan..." : "Speler Opslaan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Stats Dialog */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Statistieken Bewerken - {editingPlayer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goals">Doelpunten</Label>
                <Input id="goals" name="goals" type="number" min="0" value={statsData.goals} onChange={handleStatsChange} data-testid="stats-goals" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="assists">Assists</Label>
                <Input id="assists" name="assists" type="number" min="0" value={statsData.assists} onChange={handleStatsChange} data-testid="stats-assists" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="appearances">Wedstrijden</Label>
                <Input id="appearances" name="appearances" type="number" min="0" value={statsData.appearances} onChange={handleStatsChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="minutes_played">Speelminuten</Label>
                <Input id="minutes_played" name="minutes_played" type="number" min="0" value={statsData.minutes_played} onChange={handleStatsChange} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="yellow_cards">Gele Kaarten</Label>
                <Input id="yellow_cards" name="yellow_cards" type="number" min="0" value={statsData.yellow_cards} onChange={handleStatsChange} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="red_cards">Rode Kaarten</Label>
                <Input id="red_cards" name="red_cards" type="number" min="0" value={statsData.red_cards} onChange={handleStatsChange} />
              </div>
            </div>
            {editingPlayer?.position === "Keeper" && (
              <div className="space-y-2">
                <Label htmlFor="clean_sheets">Clean Sheets</Label>
                <Input id="clean_sheets" name="clean_sheets" type="number" min="0" value={statsData.clean_sheets} onChange={handleStatsChange} />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setStatsDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleSaveStats} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="stats-save-btn">
              {saving ? "Opslaan..." : "Statistieken Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
